import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Loader2, ShieldAlert, RefreshCcw, Wrench, Receipt, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { reconcilePastDue, auditDiscountRecipients } from '../services/api';

/**
 * StripeAuditPanel
 * Two owner-only utilities:
 *   1. Reconcile past_due users with Stripe (dry-run + apply-fixes mode)
 *   2. Audit discount recipients to see if intro discounts have expired
 *
 * Each utility is a separate collapsible section. Read-only by default; any
 * write action requires an explicit confirm step.
 */

const VERDICT_STYLE = {
  // Past-due reconcile verdicts
  active_in_stripe: { label: 'Active in Stripe (DB stale)', color: 'bg-yellow-700 text-yellow-100' },
  still_retrying: { label: 'Genuinely retrying', color: 'bg-blue-800 text-blue-100' },
  freeloading: { label: 'FREELOADING', color: 'bg-red-700 text-white' },
  paypal_active: { label: 'PAYING via PayPal', color: 'bg-emerald-700 text-emerald-100' },
  paypal_sub: { label: 'PayPal sub', color: 'bg-gray-700 text-gray-200' },
  needs_manual_review: { label: 'Needs review (PayPal unverified)', color: 'bg-orange-700 text-orange-100' },
  no_subscription_id: { label: 'No sub ID', color: 'bg-gray-700 text-gray-200' },
  error: { label: 'Error', color: 'bg-red-900 text-red-200' },
  // Discount audit verdicts
  still_discounted: { label: 'STILL discounted', color: 'bg-amber-700 text-amber-100' },
  discount_expired_now_full_price: { label: 'Discount expired ✓', color: 'bg-green-700 text-green-100' },
  never_had_discount: { label: 'Never discounted', color: 'bg-gray-700 text-gray-300' },
  subscription_not_found_in_stripe: { label: 'Sub missing', color: 'bg-red-700 text-white' },
};

const VerdictBadge = ({ verdict }) => {
  const style = VERDICT_STYLE[verdict] || { label: verdict || '?', color: 'bg-gray-700 text-gray-200' };
  return (
    <Badge className={`${style.color} font-headline tracking-wider text-[10px]`}>
      {style.label}
    </Badge>
  );
};

const formatCents = (cents, currency) => {
  if (cents == null) return '—';
  const symbol = currency === 'usd' ? '$' : `${(currency || '').toUpperCase()} `;
  return `${symbol}${(cents / 100).toFixed(2)}`;
};

const StripeAuditPanel = () => {
  // ── Past-due reconcile state ───────────────────────────────────────────
  const [pastDueLoading, setPastDueLoading] = useState(false);
  const [pastDueData, setPastDueData] = useState(null);
  const [applyingFixes, setApplyingFixes] = useState(false);

  const runPastDueDryRun = async () => {
    setPastDueLoading(true);
    try {
      const data = await reconcilePastDue(false);
      setPastDueData(data);
      toast({
        title: 'Reconcile complete',
        description: `Checked ${data.summary.total_past_due} past_due users against Stripe`,
      });
    } catch (err) {
      toast({
        title: 'Reconcile failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setPastDueLoading(false);
    }
  };

  const applyPastDueFixes = async () => {
    if (!pastDueData) return;
    const freeloaders = pastDueData.summary.freeloading || 0;
    const stale = pastDueData.summary.active_in_stripe || 0;
    const paypalActive = pastDueData.summary.paypal_active || 0;
    if (freeloaders === 0 && stale === 0 && paypalActive === 0) {
      toast({ title: 'Nothing to fix', description: 'No drift detected.' });
      return;
    }
    const ok = window.confirm(
      `This will:\n` +
      `  • Downgrade ${freeloaders} confirmed freeloader(s) to Free tier\n` +
      `  • Flip ${stale} stale past_due user(s) back to Active (Stripe says they paid)\n` +
      `  • Restore ${paypalActive} user(s) who are paying via PayPal back to Active\n\n` +
      `Proceed?`
    );
    if (!ok) return;

    setApplyingFixes(true);
    try {
      const data = await reconcilePastDue(true);
      setPastDueData(data);
      toast({
        title: 'Fixes applied',
        description: `${data.summary.fixed} user record(s) updated.`,
      });
    } catch (err) {
      toast({
        title: 'Apply failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setApplyingFixes(false);
    }
  };

  // ── Discount audit state ───────────────────────────────────────────────
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountData, setDiscountData] = useState(null);
  const [discountFilter, setDiscountFilter] = useState('still_discounted');

  const runDiscountAudit = async () => {
    setDiscountLoading(true);
    try {
      const data = await auditDiscountRecipients();
      setDiscountData(data);
      toast({
        title: 'Discount audit complete',
        description: `Checked ${data.summary.total_checked} paid subscribers`,
      });
    } catch (err) {
      toast({
        title: 'Audit failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setDiscountLoading(false);
    }
  };

  const filteredDiscountUsers = (discountData?.users || []).filter((u) => {
    if (discountFilter === 'all') return true;
    return u.verdict === discountFilter;
  });

  return (
    <Card className="bg-gray-900/90 border-2 border-red-900/40 mb-6" data-testid="stripe-audit-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-500" />
          <CardTitle className="font-headline text-xl text-red-500 tracking-wider">
            STRIPE AUDIT TOOLS
          </CardTitle>
        </div>
        <CardDescription className="font-typewriter text-gray-400">
          Cross-check the local DB against Stripe&apos;s source of truth. Read-only by default;
          fixes require an explicit confirm.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">

        {/* ─── PAST DUE RECONCILE ─────────────────────────────────────── */}
        <div className="border border-amber-900/30 rounded-md p-4 bg-black/30">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <RefreshCcw className="w-4 h-4 text-amber-500" />
              <h3 className="font-headline text-amber-500 tracking-wider">PAST DUE RECONCILE</h3>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={runPastDueDryRun}
                disabled={pastDueLoading}
                className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                data-testid="run-past-due-btn"
              >
                {pastDueLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCcw className="w-4 h-4 mr-2" />
                )}
                Run check
              </Button>
              {pastDueData && (pastDueData.summary.freeloading > 0 || pastDueData.summary.active_in_stripe > 0 || pastDueData.summary.paypal_active > 0) && (
                <Button
                  onClick={applyPastDueFixes}
                  disabled={applyingFixes}
                  className="bg-red-700 hover:bg-red-800 text-white font-headline tracking-wider"
                  data-testid="apply-past-due-fixes-btn"
                >
                  {applyingFixes ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wrench className="w-4 h-4 mr-2" />
                  )}
                  Apply fixes
                </Button>
              )}
            </div>
          </div>
          <p className="text-gray-400 font-typewriter text-xs mb-4">
            For every <code className="text-amber-400">past_due</code> user in the DB, asks Stripe
            what the subscription&apos;s current status actually is. Surfaces members whose Stripe
            sub has already been canceled (missed webhook) but who still get paid access.
          </p>

          {pastDueData && (
            <>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 mb-3 text-xs font-typewriter">
                <Badge className="bg-gray-800 text-gray-200">
                  Total: {pastDueData.summary.total_past_due}
                </Badge>
                <Badge className="bg-blue-800 text-blue-100">
                  Retrying: {pastDueData.summary.still_retrying}
                </Badge>
                <Badge className="bg-yellow-700 text-yellow-100">
                  DB stale (paid in Stripe): {pastDueData.summary.active_in_stripe}
                </Badge>
                {pastDueData.summary.paypal_active > 0 && (
                  <Badge className="bg-emerald-700 text-emerald-100">
                    Paying via PayPal: {pastDueData.summary.paypal_active}
                  </Badge>
                )}
                <Badge className="bg-red-700 text-white">
                  Freeloading: {pastDueData.summary.freeloading}
                </Badge>
                {pastDueData.summary.paypal_subs > 0 && (
                  <Badge className="bg-gray-700 text-gray-200">
                    PayPal: {pastDueData.summary.paypal_subs}
                  </Badge>
                )}
                {pastDueData.summary.no_subscription_id > 0 && (
                  <Badge className="bg-gray-700 text-gray-200">
                    No sub ID: {pastDueData.summary.no_subscription_id}
                  </Badge>
                )}
                {pastDueData.summary.errors > 0 && (
                  <Badge className="bg-orange-700 text-orange-100">
                    Need review: {pastDueData.summary.errors}
                  </Badge>
                )}
              </div>

              {pastDueData.users.length === 0 ? (
                <div className="flex items-center gap-2 text-green-400 font-typewriter text-sm py-4">
                  <CheckCircle2 className="w-4 h-4" />
                  No past_due users in the DB — nothing to reconcile.
                </div>
              ) : (
                <div className="rounded-md border border-amber-900/30 overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-amber-900/10">
                      <TableRow className="border-amber-900/30">
                        <TableHead className="font-headline text-amber-500 tracking-wide">Email</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Tier</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Stripe says</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Last paid</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Canceled</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Verdict</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Action taken</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pastDueData.users.map((u) => (
                        <TableRow key={u.userId} className="border-amber-900/30 hover:bg-amber-900/10">
                          <TableCell className="font-typewriter text-sm text-gray-200">
                            <div>{u.displayName || '—'}</div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </TableCell>
                          <TableCell className="font-typewriter text-sm uppercase">{u.tier}</TableCell>
                          <TableCell className="font-typewriter text-xs text-gray-300">
                            {u.stripeStatus || '—'}
                          </TableCell>
                          <TableCell className="font-typewriter text-xs text-gray-300">
                            {u.lastPaidAt ? (
                              <div>
                                <div>{new Date(u.lastPaidAt).toLocaleDateString()}</div>
                                <div className="text-amber-400 text-[10px]">{u.daysSinceLastPaid}d ago</div>
                              </div>
                            ) : <span className="text-gray-600">—</span>}
                          </TableCell>
                          <TableCell className="font-typewriter text-xs text-gray-300">
                            {u.canceledAt ? (
                              <div>
                                <div>{new Date(u.canceledAt).toLocaleDateString()}</div>
                                <div className={`text-[10px] ${u.daysSinceCanceled > 14 ? 'text-red-400' : 'text-amber-400'}`}>
                                  {u.daysSinceCanceled}d ago
                                </div>
                                {u.cancellationReason && (
                                  <div className="text-gray-500 text-[10px] capitalize">{u.cancellationReason.replace(/_/g, ' ')}</div>
                                )}
                              </div>
                            ) : <span className="text-gray-600">—</span>}
                          </TableCell>
                          <TableCell>
                            <VerdictBadge verdict={u.verdict} />
                          </TableCell>
                          <TableCell className="font-typewriter text-xs">
                            {u.actionTaken ? (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                {u.actionTaken.replace(/_/g, ' ')}
                              </span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <p className="text-[10px] font-typewriter text-gray-600 mt-2">
                Last run: {new Date(pastDueData.generated_at).toLocaleString()}
                {pastDueData.applied_fixes && <span className="text-green-500 ml-2">· fixes applied</span>}
              </p>
            </>
          )}
        </div>

        {/* ─── DISCOUNT AUDIT ──────────────────────────────────────────── */}
        <div className="border border-amber-900/30 rounded-md p-4 bg-black/30">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-amber-500" />
              <h3 className="font-headline text-amber-500 tracking-wider">DISCOUNT EXPIRY AUDIT</h3>
            </div>
            <Button
              onClick={runDiscountAudit}
              disabled={discountLoading}
              className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
              data-testid="run-discount-audit-btn"
            >
              {discountLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Receipt className="w-4 h-4 mr-2" />
              )}
              Run audit
            </Button>
          </div>
          <p className="text-gray-400 font-typewriter text-xs mb-4">
            For every paid subscriber, asks Stripe whether they currently have an active discount
            applied. Surfaces members whose intro coupon may not have expired yet — and confirms
            who is now being charged the full sticker price.
          </p>

          {discountData && (
            <>
              <div className="flex flex-wrap gap-2 mb-3 text-xs font-typewriter">
                <Badge className="bg-gray-800 text-gray-200">
                  Checked: {discountData.summary.total_checked}
                </Badge>
                <Badge className="bg-amber-700 text-amber-100">
                  Still discounted: {discountData.summary.currently_discounted}
                </Badge>
                <Badge className="bg-green-700 text-green-100">
                  Expired (full price): {discountData.summary.discount_expired_full_price}
                </Badge>
                <Badge className="bg-gray-700 text-gray-300">
                  Never had one: {discountData.summary.never_had_discount}
                </Badge>
                {discountData.summary.subscription_not_found > 0 && (
                  <Badge className="bg-red-700 text-white">
                    Missing in Stripe: {discountData.summary.subscription_not_found}
                  </Badge>
                )}
                {discountData.summary.errors > 0 && (
                  <Badge className="bg-red-900 text-red-200">
                    Errors: {discountData.summary.errors}
                  </Badge>
                )}
              </div>

              {/* Filter dropdown */}
              <div className="flex gap-2 mb-3 flex-wrap">
                {[
                  { value: 'still_discounted', label: 'Still discounted', color: 'amber' },
                  { value: 'discount_expired_now_full_price', label: 'Expired (now full price)', color: 'green' },
                  { value: 'never_had_discount', label: 'Never discounted', color: 'gray' },
                  { value: 'all', label: 'All', color: 'gray' },
                ].map((opt) => {
                  const active = discountFilter === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDiscountFilter(opt.value)}
                      className={`px-3 py-1 rounded text-xs font-headline tracking-wider border transition-all ${
                        active
                          ? 'bg-amber-600 text-black border-amber-500'
                          : 'border-amber-900/40 text-gray-400 hover:text-amber-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {filteredDiscountUsers.length === 0 ? (
                <div className="flex items-center gap-2 text-gray-400 font-typewriter text-sm py-4">
                  <AlertTriangle className="w-4 h-4" />
                  No users match this filter.
                </div>
              ) : (
                <div className="rounded-md border border-amber-900/30 overflow-x-auto max-h-[500px]">
                  <Table>
                    <TableHeader className="bg-amber-900/10 sticky top-0">
                      <TableRow className="border-amber-900/30">
                        <TableHead className="font-headline text-amber-500 tracking-wide">Email</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Tier</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Current price</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Discount</TableHead>
                        <TableHead className="font-headline text-amber-500 tracking-wide">Verdict</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDiscountUsers.map((u) => (
                        <TableRow key={u.userId} className="border-amber-900/30 hover:bg-amber-900/10">
                          <TableCell className="font-typewriter text-sm text-gray-200">
                            <div>{u.displayName || '—'}</div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </TableCell>
                          <TableCell className="font-typewriter text-sm uppercase">{u.tier}</TableCell>
                          <TableCell className="font-typewriter text-sm text-amber-300">
                            {formatCents(u.currentPriceCents, u.currency)}
                            {u.interval && <span className="text-gray-500 text-xs">/{u.interval}</span>}
                          </TableCell>
                          <TableCell className="font-typewriter text-xs text-gray-300">
                            {u.verdict === 'still_discounted' ? (
                              <div>
                                <div className="text-amber-400">
                                  {u.percentOff
                                    ? `${u.percentOff}% off`
                                    : u.amountOff
                                      ? `${formatCents(u.amountOff, u.currency)} off`
                                      : 'discount'}
                                </div>
                                <div className="text-gray-500">
                                  {u.duration === 'once' && 'one-time'}
                                  {u.duration === 'forever' && 'FOREVER'}
                                  {u.duration === 'repeating' && `${u.durationInMonths} months`}
                                </div>
                                {u.discountEndsAt && (
                                  <div className="text-gray-500 text-[10px]">
                                    ends {new Date(u.discountEndsAt).toLocaleDateString()}
                                  </div>
                                )}
                                {u.couponId && (
                                  <div className="text-gray-600 text-[10px]">{u.couponId}</div>
                                )}
                              </div>
                            ) : u.lastDiscount ? (
                              <span className="text-gray-500">
                                Past: {u.lastDiscount.percentOff
                                  ? `${u.lastDiscount.percentOff}% off`
                                  : formatCents(u.lastDiscount.amountOff, u.currency)}
                              </span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <VerdictBadge verdict={u.verdict} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <p className="text-[10px] font-typewriter text-gray-600 mt-2">
                Last run: {new Date(discountData.generated_at).toLocaleString()}
                {' · '}showing {filteredDiscountUsers.length} of {discountData.users.length}
              </p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StripeAuditPanel;
